import {
	GridIcon,
	Invoice02Icon,
	Package01Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import {
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { useMemo } from "react";
import { PageContent } from "@/components/layout/page-content";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MultiSelectCombobox } from "@/components/ui/multi-select-combobox";
import { DateRangePicker } from "@/components/ui/range-date-picker";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BillDetailModal } from "@/features/sales/components/bill-detail-modal";
import { BillsTabContent } from "@/features/sales/components/bills-tab-content";
import { CategoriesTabContent } from "@/features/sales/components/categories-tab-content";
import { ProductsTabContent } from "@/features/sales/components/products-tab-content";
import { useDisplaySettings } from "@/features/sales/hooks/use-display-settings";
import { useSaleActions } from "@/features/sales/hooks/use-sale-actions";
import { useSalesNavigation } from "@/features/sales/hooks/use-sales-navigation";
import { useSalesPageData } from "@/features/sales/hooks/use-sales-page-data";
import { useSalesPageState } from "@/features/sales/hooks/use-sales-page-state";
import { PAGE_SIZE } from "@/features/sales/lib/constants";
import type { SalesSearch } from "@/features/sales/lib/types";
import { usePagination } from "@/hooks/use-pagination";
import { formatBaht } from "@/lib/currency";
import { copyTableToClipboard } from "@/lib/utils/clipboard";

export const Route = createFileRoute("/(dashboard)/sales")({
	component: SalesPage,
	validateSearch: (search: Record<string, unknown>): SalesSearch => ({
		tab: (search.tab as SalesSearch["tab"]) || "bills",
		branches: (search.branches as string) || "",
		startDate: search.startDate as string | undefined,
		endDate: search.endDate as string | undefined,
	}),
});

function SalesPage() {
	const navigate = useNavigate();
	const {
		tab,
		branches: branchesParam,
		startDate,
		endDate,
	} = useSearch({ from: "/(dashboard)/sales" });

	const { pagination, onPaginationChange, resetPage } =
		usePagination(PAGE_SIZE);

	const {
		displaySettings,
		hasChanges,
		handleReset,
		handleSetDefault,
		updateSetting,
		updateColumnVisibility,
	} = useDisplaySettings();

	const pageState = useSalesPageState({ resetPage });
	const { handleTabChange, handleBranchesChange, handleDateRangeChange } =
		useSalesNavigation({ navigate, resetPage });

	const data = useSalesPageData({
		tab,
		branchesParam,
		startDate,
		endDate,
		debouncedBillsSearch: pageState.debouncedBillsSearch,
		debouncedProductsSearch: pageState.debouncedProductsSearch,
		debouncedCategoriesSearch: pageState.debouncedCategoriesSearch,
		sortKey: displaySettings.sortKey,
		selectedSaleId: pageState.selectedSaleId,
		selectedSaleBranchId: pageState.selectedSaleBranchId,
		detailModalOpen: pageState.detailModalOpen,
		pagination,
		resetPage,
	});

	const actions = useSaleActions({
		refundSale: data.refundSale,
		cancelSale: data.cancelSale,
		saleDetails: data.saleDetails,
		setDetailModalOpen: pageState.setDetailModalOpen,
	});

	const productAndCategoryFilters = useMemo(
		() =>
			data.salesFilters.filter((f) =>
				["categories", "cashier", "tabCreator"].includes(f.key),
			),
		[data.salesFilters],
	);

	return (
		<PageContent title="Sales">
			<div className="flex w-full min-w-0 flex-col gap-4 sm:gap-6">
				{/* Selectors */}
				<div className="flex flex-wrap items-center justify-start gap-3">
					<MultiSelectCombobox
						options={data.branchOptions}
						selected={data.selectedBranchIds}
						onChange={handleBranchesChange}
						placeholder="All branches"
						searchPlaceholder="Search branches..."
						emptyMessage="No branches found"
						className="w-48"
						shortcut="B"
						createNewPrefix="branch"
					/>
					<DateRangePicker
						value={data.dateRange}
						onChange={handleDateRangeChange}
					/>
				</div>

				{/* Tabs */}
				<Tabs
					value={tab}
					onValueChange={handleTabChange}
					className="flex w-full flex-col"
				>
					<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-neutral-100">
						<TabsList variant="line" className="gap-12">
							<TabsTrigger
								value="bills"
								className="flex items-center gap-2.5 px-0 pb-3.5"
							>
								<HugeiconsIcon
									icon={Invoice02Icon}
									size={18}
									strokeWidth={1.5}
								/>
								By Bills
							</TabsTrigger>
							<TabsTrigger
								value="products"
								className="flex items-center gap-2.5 px-0 pb-3.5"
							>
								<HugeiconsIcon
									icon={Package01Icon}
									size={18}
									strokeWidth={1.5}
								/>
								By Products
							</TabsTrigger>
							<TabsTrigger
								value="categories"
								className="flex items-center gap-2.5 px-0 pb-3.5"
							>
								<HugeiconsIcon icon={GridIcon} size={18} strokeWidth={1.5} />
								By Categories
							</TabsTrigger>
						</TabsList>
					</div>

					<TabsContent
						value="bills"
						className="mt-4 flex w-full flex-col outline-none"
					>
						<BillsTabContent
							search={pageState.billsSearch}
							onSearchChange={pageState.handleBillsSearchChange}
							filters={data.salesFilters}
							filterState={data.filterState}
							displaySettings={displaySettings}
							onShowHeadersChange={(checked) =>
								updateSetting("showHeaders", checked)
							}
							onViewModeChange={(mode) => updateSetting("viewMode", mode)}
							onSortChange={(sort) => updateSetting("sortKey", sort)}
							onPropertyChange={updateColumnVisibility}
							onReset={handleReset}
							onSetDefault={handleSetDefault}
							hasChanges={hasChanges}
							isCopied={pageState.isCopied}
							onCopy={() => {
								copyTableToClipboard(data.paginatedSales, [
									{ header: "Invoice", getValue: (s) => s.invoiceNo },
									{
										header: "Date",
										getValue: (s) => {
											const date = s.createdAt ?? s._creationTime;
											return date
												? new Date(date).toLocaleString("en-GB", {
														dateStyle: "short",
														timeStyle: "short",
													})
												: "—";
										},
									},
									{ header: "Status", getValue: (s) => s.status },
									{ header: "Total", getValue: (s) => formatBaht(s.total) },
									{ header: "Payment", getValue: (s) => s.paymentMethod || "" },
									{ header: "Cashier", getValue: (s) => s.staffName || "" },
									{ header: "Customer", getValue: (s) => s.customerName || "" },
								]);
								pageState.startCopyFeedback();
							}}
							data={data.paginatedSales}
							loading={data.isLoadingSales}
							pagination={pagination}
							onPaginationChange={onPaginationChange}
							rowCount={data.billsTotal}
							onView={pageState.handleViewSale}
							onRefund={actions.handleRefundClick}
							onCancel={actions.handleCancelClick}
							branches={data.branches}
						/>
					</TabsContent>

					<TabsContent
						value="products"
						className="mt-4 flex w-full flex-col outline-none"
					>
						<ProductsTabContent
							search={pageState.productsSearch}
							onSearchChange={pageState.handleProductsSearchChange}
							filters={productAndCategoryFilters}
							filterState={data.filterState}
							displaySettings={displaySettings}
							onShowHeadersChange={(checked) =>
								updateSetting("showHeaders", checked)
							}
							onViewModeChange={(mode) => updateSetting("viewMode", mode)}
							onSortChange={(sort) => updateSetting("sortKey", sort)}
							onPropertyChange={updateColumnVisibility}
							onReset={handleReset}
							onSetDefault={handleSetDefault}
							hasChanges={hasChanges}
							isCopied={pageState.isCopied}
							onCopy={() => {
								copyTableToClipboard(data.filteredProductSales, [
									{ header: "Product", getValue: (p) => p.name ?? "" },
									{ header: "SKU", getValue: (p) => p.sku || "" },
									{ header: "Category", getValue: (p) => p.categoryName || "" },
									{
										header: "Gross Revenue",
										getValue: (p) => formatBaht(p.revenue ?? 0),
									},
									{
										header: "Quantity Sold",
										getValue: (p) => p.quantitySold ?? 0,
									},
									{
										header: "Transactions",
										getValue: (p) => p.transactionCount ?? 0,
									},
								]);
								pageState.startCopyFeedback();
							}}
							data={data.paginatedProductSales}
							loading={data.isLoadingProducts}
							pagination={pagination}
							onPaginationChange={onPaginationChange}
							rowCount={data.filteredProductSales.length}
						/>
					</TabsContent>

					<TabsContent
						value="categories"
						className="mt-4 flex w-full flex-col outline-none"
					>
						<CategoriesTabContent
							search={pageState.categoriesSearch}
							onSearchChange={pageState.handleCategoriesSearchChange}
							filters={productAndCategoryFilters}
							filterState={data.filterState}
							displaySettings={displaySettings}
							onShowHeadersChange={(checked) =>
								updateSetting("showHeaders", checked)
							}
							onViewModeChange={(mode) => updateSetting("viewMode", mode)}
							onSortChange={(sort) => updateSetting("sortKey", sort)}
							onPropertyChange={updateColumnVisibility}
							onReset={handleReset}
							onSetDefault={handleSetDefault}
							hasChanges={hasChanges}
							isCopied={pageState.isCopied}
							onCopy={() => {
								copyTableToClipboard(data.filteredCategorySales, [
									{
										header: "Category",
										getValue: (c) => c.name || "Uncategorized",
									},
									{
										header: "Gross Revenue",
										getValue: (c) => formatBaht(c.revenue ?? 0),
									},
									{
										header: "Transactions",
										getValue: (c) => c.transactionCount ?? 0,
									},
									{ header: "Products", getValue: (c) => c.productCount ?? 0 },
								]);
								pageState.startCopyFeedback();
							}}
							data={data.paginatedCategorySales}
							loading={data.isLoadingCategories}
							pagination={pagination}
							onPaginationChange={onPaginationChange}
							rowCount={data.filteredCategorySales.length}
						/>
					</TabsContent>
				</Tabs>
			</div>

			<BillDetailModal
				open={pageState.detailModalOpen}
				onOpenChange={pageState.setDetailModalOpen}
				sale={data.saleDetails ?? null}
				loading={data.isLoadingDetails}
				onRefund={actions.handleDetailRefund}
				onCancel={actions.handleDetailCancel}
				isRefunding={actions.isRefunding}
				isCancelling={actions.isCancelling}
			/>

			<AlertDialog
				open={actions.refundDialogOpen}
				onOpenChange={actions.setRefundDialogOpen}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Refund sale?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to refund sale "
							{actions.saleToRefund?.invoiceNo}"? This action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={actions.isRefunding}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={actions.confirmRefund}
							disabled={actions.isRefunding}
						>
							{actions.isRefunding ? "Refunding..." : "Refund"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog
				open={actions.cancelDialogOpen}
				onOpenChange={actions.setCancelDialogOpen}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Cancel sale?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to cancel sale "
							{actions.saleToCancel?.invoiceNo}"? This action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={actions.isCancelling}>
							Keep sale
						</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={actions.confirmCancel}
							disabled={actions.isCancelling}
						>
							{actions.isCancelling ? "Cancelling..." : "Cancel Sale"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</PageContent>
	);
}
